/**
 * Extracts tenant name from the current hostname.
 * Follows the same pattern as backend: splits hostname on '.' and takes the first part.
 * 
 * Example:
 * - 'tenant1.localhost' -> 'tenant1'
 * - 'tenant2.localhost:4200' -> 'tenant2' (port is ignored)
 * - 'tenant3.yourdomain.com' -> 'tenant3'
 * 
 * @returns The tenant name extracted from hostname
 * @throws Error if tenant cannot be extracted (app should halt)
 */
export function getTenantFromHostname(): string {
  if (typeof window === 'undefined') {
    throw new Error('Tenant extraction failed: window object is not available. This code must run in a browser environment.');
  }

  const hostname = window.location.hostname;
  
  if (!hostname || hostname.trim() === '') {
    throw new Error('Tenant extraction failed: hostname is empty or undefined. The application cannot proceed without a valid tenant.');
  }

  // Split hostname on '.' and take the first part (tenant name)
  // This matches the backend pattern: strings.Split(hostOnly, ".") and parts[0]
  const parts = hostname.split('.');
  
  if (parts.length === 0 || !parts[0] || parts[0].trim() === '') {
    throw new Error(`Tenant extraction failed: Unable to extract tenant from hostname "${hostname}". Hostname must follow the pattern: {tenant}.domain.com`);
  }

  const tenantName = parts[0].trim();

  // Validate tenant name is not just 'localhost' or common domain names
  const invalidTenants = ['localhost', 'www', 'api'];
  if (invalidTenants.includes(tenantName.toLowerCase())) {
    throw new Error(`Tenant extraction failed: Hostname "${hostname}" does not contain a valid tenant subdomain. Expected format: {tenant}.localhost or {tenant}.yourdomain.com`);
  }

  return tenantName;
}

/**
 * Gets the tenant name and throws an error if extraction fails.
 * This function should be called at app initialization to ensure tenant is available.
 * 
 * @returns The tenant name
 * @throws Error if tenant cannot be extracted
 */
export function requireTenant(): string {
  return getTenantFromHostname();
}

